import { useState } from "react";
import { NavLink, matchPath, useLocation } from "react-router-dom";
import {
  CaretDown, CaretRight, CurrencyInr, Wrench, UsersThree, Package, ChartLineUp, SquaresFour, Wallet, Database, ShieldCheck,
} from "@phosphor-icons/react";

const GROUP_STYLE = {
  Sales: { color: "#2563EB", icon: CurrencyInr },
  Service: { color: "#EA580C", icon: Wrench },
  HR: { color: "#0D9488", icon: UsersThree },
  Accessories: { color: "#9333EA", icon: Package },
  Reporting: { color: "#16A34A", icon: ChartLineUp },
  Accounts: { color: "#0891B2", icon: Wallet },
  General: { color: "#CA8A04", icon: SquaresFour },
  Masters: { color: "#475569", icon: Database },
  Admin: { color: "#DC2626", icon: ShieldCheck },
};

const STORE = "nav-open-groups";

const readOpen = () => {
  try {
    return JSON.parse(localStorage.getItem(STORE) || "{}");
  } catch {
    return {};
  }
};

function NavItem({ item, nested }) {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.to}
      end={item.end}
      data-testid={item.id}
      className={({ isActive }) =>
        `flex items-center gap-3 ${nested ? "pl-6 pr-3 py-1.5" : "px-3 py-2"} text-sm transition-colors duration-150 border ${
          isActive ? "bg-ink text-white border-ink" : "border-transparent text-ink hover:border-canvas-line hover:bg-white"
        }`
      }
    >
      <Icon size={nested ? 14 : 16} weight="bold" />
      <span className="font-semibold tracking-tight">{item.label}</span>
    </NavLink>
  );
}

export default function NavGroups({ items }) {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(readOpen);
  const isActive = (it) => !!matchPath({ path: it.to, end: !!it.end || it.to === "/" }, pathname);

  const sections = [];
  for (const it of items) {
    const last = sections[sections.length - 1];
    if (it.group && last?.group === it.group) last.items.push(it);
    else sections.push({ group: it.group, items: [it] });
  }

  const toggle = (g, current) => {
    const next = { ...open, [g]: !current };
    setOpen(next);
    localStorage.setItem(STORE, JSON.stringify(next));
  };

  return (
    <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1" data-testid="sidebar-nav">
      {sections.map((sec) => {
        if (!sec.group) return sec.items.map((it) => <NavItem key={it.to} item={it} />);
        const hasActive = sec.items.some(isActive);
        const expanded = open[sec.group] ?? hasActive;
        const slug = sec.group.toLowerCase().replace(/[^a-z]+/g, "-");
        const { color, icon: GIcon } = GROUP_STYLE[sec.group] || { color: "#111111", icon: SquaresFour };
        return (
          <div key={sec.group} className="pt-1">
            <button
              type="button"
              onClick={() => toggle(sec.group, expanded)}
              data-testid={`nav-group-${slug}`}
              aria-expanded={expanded}
              style={{ borderLeftColor: color, backgroundColor: expanded || hasActive ? `${color}14` : undefined }}
              className="w-full flex items-center gap-2.5 px-3 py-2 border-l-[3px] text-[13px] font-bold uppercase tracking-wide text-ink transition-colors duration-150 hover:bg-white"
            >
              <GIcon size={16} weight="bold" style={{ color }} />
              <span className="flex-1 text-left">{sec.group}</span>
              {expanded ? <CaretDown size={13} weight="bold" style={{ color }} /> : <CaretRight size={13} weight="bold" className="text-ink-muted" />}
            </button>
            {expanded && (
              <div className="space-y-0.5 mt-0.5 ml-[3px] border-l" style={{ borderLeftColor: `${color}55` }} data-testid={`nav-group-${slug}-items`}>
                {sec.items.map((it) => <NavItem key={it.to} item={it} nested />)}
              </div>
            )}
          </div>
        );
      })}
    </nav>
  );
}
